import Box from '@mui/material/Box'
import { FunctionComponent, useEffect, useState } from 'react'
import CategoryLayout from 'components/Layout/Category'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'

interface ProgressiveDisclosureProps {
  children: React.ReactElement
  validator: (value: string) => boolean
  value: string | string[]
  disableDisclose?: boolean
  /**
   * When true, children are returned as-is without being wrapped in
   * Box + CategoryLayout. Use this when the component is already rendered
   * inside a container that provides its own layout (e.g. PersonalForm).
   */
  disableLayout?: boolean
  testId?: string
  path?: string
}

const styles = () => ({
  undisclosedBox: {
    display: 'none'
  }
})

const ProgressiveDisclosure: FunctionComponent<ProgressiveDisclosureProps> = ({
  children,
  validator,
  value,
  disableDisclose,
  disableLayout,
  testId,
  path
}) => {
  const [isPathCompleted, setIsPathCompleted] = useState(false)
  const { undisclosedBox } = useEmotionStyles(styles)
  const {
    traversal: { removeErrorsForPath, getErrorsForPath }
  } = useStores()

  /* rendering a component progressively caused re-rendering due to wrappers that we have
  to solve this we will render the components but hide them */

  const visibleElement = disableLayout ? (
    <>{children}</>
  ) : /* to maintain nesting of text elements we return children directly for caption elements */
  children.props.variant === 'caption' ? (
    children
  ) : (
    <Box data-testid={testId}>
      <CategoryLayout>{children}</CategoryLayout>
    </Box>
  )

  const isValid = Array.isArray(value)
    ? value.every((singleValue) => validator(singleValue))
    : validator(value)

  useEffect(() => {
    if (disableDisclose || isPathCompleted || !isValid || !path) {
      return
    }

    trackInAmplitude(AmplitudeEvent.ProgressiveDisclosurePathCompleted, {
      path
    })
    setIsPathCompleted(true)
  }, [disableDisclose, isPathCompleted, isValid, path])

  useEffect(() => {
    if (disableDisclose || isPathCompleted || isValid || !path) {
      return
    }

    if (getErrorsForPath(path).length > 0) {
      removeErrorsForPath(path)
    }
  }, [
    disableDisclose,
    getErrorsForPath,
    isPathCompleted,
    isValid,
    path,
    removeErrorsForPath
  ])

  if (isPathCompleted || disableDisclose) {
    return visibleElement
  }

  if (isValid) {
    return visibleElement
  }

  return disableLayout ? (
    <div style={{ display: 'none' }} data-testid={testId}>
      {children}
    </div>
  ) : (
    <Box className={undisclosedBox} data-testid={testId}>
      <CategoryLayout>{children}</CategoryLayout>
    </Box>
  )
}

export default ProgressiveDisclosure
